import { ReactComponent as Whatsapp } from '@shared/assets/icons/ic-whatsapp-black.svg'
import {
  AudioLines,
  BadgeCheck,
  Bell,
  Blocks,
  ChartPie,
  Code,
  Globe,
  Hash,
  HomeIcon,
  Library,
  Logs,
  MessageCircle,
  MessageSquare,
  Phone,
  Settings2,
  ShieldCheck,
  Smartphone,
  SquareAsterisk,
  Waypoints,
  Workflow,
} from 'lucide-react'
import { Navigate, useSearchParams } from 'router-v6'
import { VoiceGated } from 'v2/api-console/shared/components/voice-gated'
import { LandingDashboard } from 'v2/pages/home/landing-dashboard'
import { getReportSections } from 'v2/pages/reports/report-sections'
import { isOnlyAgentRole } from 'v2/shared/utils'
import {
  LOCKED_TOOLTIP,
  openBuddyEnterpriseRequest,
} from 'v2/shared/utils/buddy-enterprise-request'
import { createLazyComponent } from 'v2/shared/utils/lazy-utils'
import {
  agentsDenialReason,
  canViewAgents,
  canViewBuildSection,
  canViewCompliance,
  canViewGeoPermissions,
  canViewLogs,
  canViewMonitorSection,
  canViewUcc,
  canViewUsageReports,
  isSupportOnly,
  isUccReviewerOnly,
  type RouteMeta,
} from 'v2/shared/utils/role-based-visibility'
import type { IRoute } from 'v2/types'
import { SidebarGroups } from 'v2/types'
import type { RFeatureFlags, UserRole } from 'v2/types/api/auth'
import type { RUserProfile } from 'v2/types/api/settings'
import { type ModulePermissionTypeMap, RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { flowRoute } from './flow-route'
import { legacyConsoleRoutes } from './legacy-console'
import { type Redirect, rows } from './redirect-rows'

const PhoneNumbers = createLazyComponent(
  () => import('v2/api-console/numbers/phone-numbers-combined'),
)
const WhatsappConfig = createLazyComponent(() => import('v2/pages/settings/whatsapp'))
const ListVerifyApp = createLazyComponent(() => import('v2/pages/verify/list-verify-app'))
const VerifyApp = createLazyComponent(() => import('v2/pages/verify/verify-app'))
const VerifySessionDetail = createLazyComponent(() => import('v2/pages/verify/session-detail'))
const Applications = createLazyComponent(() => import('v2/api-console/applications'))
const SipTrunking = createLazyComponent(() => import('v2/api-console/applications/sip-trunking'))
const ListFlows = createLazyComponent(() => import('v2/pages/settings/flows/list-flows'))
const AgentDocs = createLazyComponent(() => import('v2/pages/agent-docs'))
// Standalone (org-wide) Knowledge Base management — the same components the agent-builder KB tab
// reuses, but reachable directly from nav (previously these existed but were never routed).
const ListKnowledgeBase = createLazyComponent(() => import('v2/pages/flow/knowledge-base/list'))
const NewKnowledgeBase = createLazyComponent(() => import('v2/pages/flow/knowledge-base/new'))
const KnowledgeBaseDetail = createLazyComponent(() => import('v2/pages/flow/knowledge-base/detail'))
const KnowledgeBaseDetailUpload = createLazyComponent(
  () => import('v2/pages/flow/knowledge-base/detail-upload'),
)

// New placeholder pages
const BuddyRedirect = createLazyComponent(() => import('v2/pages/buddy'))
const AudioStreaming = createLazyComponent(() => import('v2/pages/audio-streaming'))
const MessagingAgents = createLazyComponent(() => import('v2/pages/messaging-agents'))
const Rcs = createLazyComponent(() => import('v2/pages/rcs'))
const ChatWidgetDomain = createLazyComponent(() => import('v2/pages/settings/chat-widget-domain'))
const ListChatApplications = createLazyComponent(
  () => import('v2/pages/settings/chat/chat-listing'),
)
const ChatSettings = createLazyComponent(() => import('v2/pages/settings/chat'))
const ExternalTools = createLazyComponent(
  () => import('v2/pages/settings/integrations/apps/external-apps'),
)
const OAuthProviders = createLazyComponent(
  () => import('v2/pages/settings/integrations/oauth-providers'),
)
const CombinedLogs = createLazyComponent(() => import('v2/pages/logs'))
const CombinedAlerting = createLazyComponent(() => import('v2/pages/alerting'))
const SecretsPage = createLazyComponent(() => import('v2/pages/flow/secrets'))
const PortInOrderDetails = createLazyComponent(
  () => import('v2/api-console/numbers/port-in/order-details'),
)
const PortInNewOrder = createLazyComponent(() => import('v2/api-console/numbers/port-in/new-order'))
const Compliance = createLazyComponent(() => import('v2/pages/10dlc'))
const ComplianceRegistration = createLazyComponent(() => import('v2/pages/10dlc/registration'))
const Reports = createLazyComponent(() => import('v2/pages/reports'))
const AIPerformance = createLazyComponent(() => import('v2/pages/reports/ai-performance'))
const AgentAvailability = createLazyComponent(() => import('v2/pages/reports/agent-availability'))
const AgentPerformance = createLazyComponent(() => import('v2/pages/reports/agent-performance'))
const ConversationsOverview = createLazyComponent(
  () => import('v2/pages/reports/conversations-overview'),
)
const CustomerMetrics = createLazyComponent(() => import('v2/pages/reports/customer-metrics'))
const OperationsOverview = createLazyComponent(() => import('v2/pages/reports/operations-overview'))
const VoiceAgentsAI = createLazyComponent(() => import('v2/pages/reports/voice-agents-gate'))
const UsageReports = createLazyComponent(() => import('v2/pages/reports/usage-reports'))
const FlowRunsPage = createLazyComponent(() => import('v2/pages/flow/flow-runs'))
const FlowRunDetailPage = createLazyComponent(() => import('v2/pages/flow/flow-runs/detail'))
const CallInsights = createLazyComponent(
  () => import('v2/api-console/voice/call-logs/call-insights'),
)
const MessageInsights = createLazyComponent(
  () => import('v2/api-console/messaging/message-logs/message-insights'),
)
const ZentrunkDetails = createLazyComponent(
  () => import('v2/api-console/zentrunk/logs/zentrunk-details'),
)

// User avatar popover settings pages. Subaccounts / messaging-settings still
// live as tabs on the Organization settings page (SER-5859) — their old
// routes below just redirect there, so they're imported fresh inside
// pages/settings/organization-settings instead of here. Endpoints is still a
// standalone popover destination. Voice Settings merged its old Voice/SIP
// Trunking sub-tabs into one page (SER-5859) — /voice-settings/sip-trunking
// below is now just an alias to the same merged page, kept for old links.
// Geo Permissions was briefly folded into Organization settings as a "Geo"
// tab and is now back to its own standalone item + route (Voice/Messaging as
// real sub-tabs) — see user-avatar-popover-sections.ts.
const Endpoints = createLazyComponent(() => import('v2/api-console/voice/endpoints'))

const VoiceSettings = createLazyComponent(() => import('v2/api-console/voice/voice-settings'))
const GeoPermissions = createLazyComponent(() => import('v2/pages/settings/geo-permissions'))

// Bridges legacy `?tab=` bookmarks to the path-based `/<base>/:tab` URLs.
const TabRedirect = ({
  basePath,
  defaultTab,
  aliases,
}: {
  basePath: string
  defaultTab: string
  aliases?: Record<string, string>
}) => {
  const [searchParams] = useSearchParams()
  const rawTab = searchParams.get('tab') || defaultTab
  const tab = aliases?.[rawTab] ?? rawTab
  return <Navigate to={`${basePath}/${tab}`} replace />
}

// Legacy path redirects: [oldPath(s), newPath]
// Can be a single string or array of strings for multiple routes redirecting to the same path
const LEGACY_REDIRECTS: Redirect[] = [
  ['/api/audio-streaming', '/audio-streaming'],
  ['/api/sip-trunking', '/sip-trunking'],
  ['/api/messaging-agents', '/messaging-agents'],
  ['/api/verify', '/verify'],
  [['/api/xml-applications', '/xml-applications'], '/applications'],
  [['/api/phone-numbers', '/settings/phone-numbers'], '/phone-numbers'],
  ['/api/whatsapp', '/whatsapp/numbers'],
  ['/api/whatsapp/numbers', '/whatsapp/numbers'],
  ['/api/whatsapp/templates', '/whatsapp/templates'],
  ['/api/rcs', '/rcs'],
  ['/api/logs', '/logs'],
  ['/api/analytics', '/analytics'],
  ['/api/alerting', '/alerting'],
  ['/api/agent-qa', '/agent-qa'],
  [['/agent/flow-runs', '/agent-flow-runs'], '/agent-runs'],
  ['/agent/list', '/agents'],
  ['/api/port-in', '/phone-numbers/port-in'],
  ['/api/port-in/new', '/port-in/new'],
  [['/reports', '/reports/:dashboards', '/analytics/:dashboard'], '/analytics'],
  // Zentrunk was rebranded to SIP Trunking; the old logs URL is referenced externally
  ['/logs/zentrunk', '/logs/sip-trunking'],
  // Example: Multiple routes redirecting to the same path
  // [['/old-path-1', '/old-path-2'], '/new-path'],
  // Detail pages: a function target carries the ID and the query string over
  ['/api/voice/call-logs/:call_uuid', (p) => `/logs/voice/${p.call_uuid}`],
  ['/api/messaging/logs/:message_uuid', (p) => `/logs/messages/${p.message_uuid}`],
  ['/agent/flow-runs/:flowId/:runId', (p) => `/agent-runs/${p.flowId}/${p.runId}`],
  ['/api/port-in/:requestId', (p) => `/port-in/${p.requestId}`],
  ['/logs/zentrunk/:call_uuid', (p) => `/logs/sip-trunking/${p.call_uuid}`],
]

// Analytics shows when it has at least one section: the getReportSections list the sidebar and
// the /analytics landing render.
const isAnalyticsHidden = (_meta?: RouteMeta) => getReportSections(_meta).length === 0

// Compliance (US messaging) is shown by default in every region except India —
// 10DLC / Toll-free registration is US-messaging-only and does not apply in the
// Indian data region. The tab is no longer gated on the submission feature flags;
// non-Enterprise plans instead get an "Upgrade to Enterprise" CTA inside the page
// (see CompliancePage). Also requires the `message_channel` view RBAC permission —
// the backend now 403s these endpoints without it.
const isComplianceHidden = (_meta?: {
  isIndianRegion?: boolean
  isUsRegion?: boolean
  permissionsMap?: ModulePermissionTypeMap
}) => !canViewCompliance(_meta)

export const getMainRoutes = (_meta?: {
  profileData?: RUserProfile
  featureFlags?: RFeatureFlags
  roles?: UserRole[]
  role?: UserRole
  permissionsMap?: ModulePermissionTypeMap
  location?: string
  isIndianRegion?: boolean
  isUsRegion?: boolean
  accountProductType?: string
  isVerifyLocked?: boolean
  /** False in the India region, where Verify is not offered at all. */
  isVerifyAvailable?: boolean
  isWhatsappLocked?: boolean
}): IRoute[] => {
  // Geo Permissions: standalone parent + 2 nested children, reachable only
  // from the user avatar popover (mirrors Endpoints).
  const geoPermissionsRoutes: IRoute[] = [
    {
      title: 'Geo Permissions',
      path: '/geo-permissions',
      icon: <Globe size={16} />,
      component: <GeoPermissions />,
      notAllowed: !canViewGeoPermissions(_meta),
      hidden: true,
      children: [
        {
          title: 'Geo Permissions - Voice',
          path: '/geo-permissions/voice',
          component: <GeoPermissions />,
          notAllowed: !canViewGeoPermissions(_meta),
          hidden: true,
        },
        {
          title: 'Geo Permissions - Messaging',
          path: '/geo-permissions/messaging',
          component: <GeoPermissions />,
          notAllowed: !canViewGeoPermissions(_meta),
          hidden: true,
        },
        {
          // SER-6024: page redirects to /voice when the
          // sipTrunkingGeoPermissionsEnabled flag is off.
          title: 'Geo Permissions - SIP Trunking',
          path: '/geo-permissions/sip-trunking',
          component: <GeoPermissions />,
          notAllowed: !canViewGeoPermissions(_meta),
          hidden: true,
        },
      ],
    },
  ]

  return [
    // ========== HOME (ungrouped — appears above section groups in sidebar) ==========
    {
      title: 'Home',
      path: '/home',
      icon: <HomeIcon size={16} />,
      component: <LandingDashboard />,
      notAllowed: isOnlyAgentRole(_meta),
      sidebar: {},
    },
    {
      title: 'Buddy',
      path: '/buddy',
      component: <BuddyRedirect />,
      hidden: true,
    },

    // ========== BUILD SECTION ==========
    {
      title: 'Agents',
      path: '/agents',
      icon: <Workflow size={16} />,
      component: <ListFlows />,
      hidden: !canViewAgents(_meta),
      notAllowed: !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
      sidebar: {
        group: SidebarGroups.build,
      },
    },
    {
      title: 'Agent Docs',
      path: '/agents/docs/*',
      component: <AgentDocs />,
      hidden: true,
      notAllowed: !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
    },
    flowRoute(_meta, 'Flow', '/flow', <ListFlows />),
    flowRoute(_meta, 'Flow Runs', '/flow/runs', <FlowRunsPage />),
    flowRoute(_meta, 'Flow Run Detail', '/flow/runs/:flowId/:runId', <FlowRunDetailPage />),
    {
      title: 'Applications',
      path: '/applications',
      component: <Applications />,
      icon: <Code size={16} />,
      notAllowed: !canViewBuildSection(_meta),
      sidebar: {
        group: SidebarGroups.build,
      },
    },
    {
      title: 'Audio Streaming',
      path: '/audio-streaming',
      component: <AudioStreaming />,
      icon: <AudioLines size={16} />,
      hidden: true,
      sidebar: {
        group: SidebarGroups.build,
      },
    },
    // Org-wide Knowledge Bases — reachable from nav (the list/detail/create components already
    // existed for the agent-builder KB tab; they just weren't routed standalone). Same view perm
    // as Agents since KBs are an AI-agent resource.
    {
      title: 'Knowledge Bases',
      path: '/agent/knowledge-base',
      icon: <Library size={16} />,
      component: <ListKnowledgeBase />,
      hidden: !canViewAgents(_meta),
      notAllowed: !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
      // De-listed from the sidebar: KBs are managed from the agent-builder "Knowledge Base" tab
      // (which now also hosts the search-engine upgrade banner). The standalone list/detail/create
      // routes stay reachable by URL (deep links) but are no longer a top-level nav item.
    },
    {
      title: 'Knowledge Bases',
      path: '/agent/knowledge-base/new',
      component: <NewKnowledgeBase />,
      hidden: true,
      notAllowed: !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
    },
    {
      title: 'Knowledge Bases',
      path: '/agent/knowledge-base/:id',
      component: <KnowledgeBaseDetail />,
      hidden: true,
      notAllowed: !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
    },
    {
      title: 'Knowledge Bases',
      path: '/agent/knowledge-base/:id/upload',
      component: <KnowledgeBaseDetailUpload />,
      hidden: true,
      notAllowed: !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
    },
    {
      title: 'Messaging Agents',
      hidden: true,
      path: '/messaging-agents',
      component: <MessagingAgents />,
      icon: <MessageSquare size={16} />,
      sidebar: {
        group: SidebarGroups.build,
      },
    },
    {
      title: 'SIP Trunking',
      path: '/sip-trunking',
      icon: <Phone size={16} />,
      component: <TabRedirect basePath="/sip-trunking" defaultTab="inbound" />,
      hidden: !canViewBuildSection(_meta),
      notAllowed: !canViewBuildSection(_meta),
      sidebar: {
        group: SidebarGroups.build,
      },
    },
    {
      title: 'SIP Trunking',
      path: '/sip-trunking/:tab',
      component: <SipTrunking />,
      hidden: true,
      notAllowed: !canViewBuildSection(_meta),
    },
    {
      title: 'Verify',
      path: '/verify',
      component: <ListVerifyApp />,
      icon: <BadgeCheck size={16} />,
      // Verify is not offered in India, so the region hides it outright rather than
      // ghosting it — a lock advertises an upgrade that does not exist there. Compared
      // against `false` explicitly: absent meta must fail OPEN, matching the hook, or a
      // caller that forgets the flag silently hides a product people are paying for.
      hidden: !canViewBuildSection(_meta) || _meta?.isVerifyAvailable === false,
      notAllowed: !canViewBuildSection(_meta) || _meta?.isVerifyAvailable === false,
      // Ghosted + opens the upgrade flow instead of navigating. Deep links still reach the
      // page, which renders the same CTA in place.
      locked: _meta?.isVerifyLocked,
      lockedTooltip: LOCKED_TOOLTIP,
      onLockedClick: () => openBuddyEnterpriseRequest({ source: 'verify' }),
      sidebar: {
        group: SidebarGroups.build,
      },
    },
    {
      title: 'Verify Application',
      path: '/verify/app/:app_uuid',
      component: <VerifyApp />,
      hidden: true,
      // Carries the same gates as /verify. Without them this deep link stayed reachable
      // by URL for roles and regions that cannot see Verify anywhere else.
      notAllowed: !canViewBuildSection(_meta) || _meta?.isVerifyAvailable === false,
    },

    // ========== DEPLOY SECTION ==========
    {
      title: 'Chat Widget',
      path: '/chat-widget',
      icon: <MessageCircle size={16} />,
      component: <ChatWidgetDomain />,
      notAllowed: !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
      hidden: !canViewAgents(_meta),
      sidebar: {
        group: SidebarGroups.deploy,
      },
    },
    {
      title: 'Chat applications',
      path: '/chat-widget/list',
      component: <ListChatApplications />,
      hidden: true,
      notAllowed: !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
    },
    {
      title: 'Edit chat application',
      path: '/chat-widget/:id',
      component: <ChatSettings />,
      hidden: true,
      notAllowed: !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
    },
    {
      title: 'Compliance',
      path: '/compliance',
      icon: <ShieldCheck size={16} />,
      component: <TabRedirect basePath="/compliance" defaultTab="drafts" />,
      hidden: isComplianceHidden(_meta),
      notAllowed: isComplianceHidden(_meta),
      sidebar: {
        group: SidebarGroups.deploy,
      },
    },
    {
      title: 'Compliance',
      path: '/compliance/:tab',
      component: <Compliance />,
      hidden: isComplianceHidden(_meta),
      notAllowed: isComplianceHidden(_meta),
    },
    {
      title: 'Compliance Registration',
      path: '/compliance/register/:id?',
      component: <ComplianceRegistration />,
      hidden: isComplianceHidden(_meta),
      notAllowed: isComplianceHidden(_meta),
    },
    {
      title: 'Compliance Brand View',
      path: '/compliance/brand/:id',
      component: <ComplianceRegistration />,
      hidden: isComplianceHidden(_meta),
      notAllowed: isComplianceHidden(_meta),
    },
    {
      title: 'Compliance Campaign View',
      path: '/compliance/campaign/:id',
      component: <ComplianceRegistration />,
      hidden: isComplianceHidden(_meta),
      notAllowed: isComplianceHidden(_meta),
    },
    {
      title: 'Compliance Tollfree View',
      path: '/compliance/tollfree/:id',
      component: <ComplianceRegistration />,
      hidden: isComplianceHidden(_meta),
      notAllowed: isComplianceHidden(_meta),
    },
    {
      title: 'External Tools',
      path: '/external-tools',
      icon: <Blocks size={16} />,
      hidden: true,
      // ExternalTools has no padding of its own; inside the agent builder its Tools tab supplies it.
      component: (
        <div className="flex h-full flex-col p-6">
          <ExternalTools />
        </div>
      ),
      sidebar: {
        group: SidebarGroups.deploy,
      },
    },
    {
      title: 'OAuth Providers',
      path: '/oauth-providers',
      icon: <Blocks size={16} />,
      hidden: true,
      component: <OAuthProviders />,
      sidebar: {
        group: SidebarGroups.deploy,
      },
    },
    {
      title: 'Phone Numbers',
      path: '/phone-numbers',
      icon: <Hash size={16} />,
      component: (
        <TabRedirect
          basePath="/phone-numbers"
          defaultTab={
            (isSupportOnly(_meta) || isUccReviewerOnly(_meta)) && canViewUcc(_meta) ? 'ucc' : 'list'
          }
          aliases={{
            'phone-numbers':
              (isSupportOnly(_meta) || isUccReviewerOnly(_meta)) && canViewUcc(_meta)
                ? 'ucc'
                : 'list',
          }}
        />
      ),
      hidden:
        !canViewUcc(_meta) &&
        !_meta?.permissionsMap?.[RbacModules.channelCalls]?.[RbacPermissionTypes.view] &&
        !_meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view],
      notAllowed:
        !canViewUcc(_meta) &&
        !_meta?.permissionsMap?.[RbacModules.channelCalls]?.[RbacPermissionTypes.view] &&
        !_meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view],
      sidebar: {
        group: SidebarGroups.deploy,
      },
    },
    {
      title: 'Phone Numbers',
      path: '/phone-numbers/:tab',
      component: <PhoneNumbers />,
      hidden: true,
      notAllowed:
        !canViewUcc(_meta) &&
        !_meta?.permissionsMap?.[RbacModules.channelCalls]?.[RbacPermissionTypes.view] &&
        !_meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view],
    },
    {
      title: 'Port-in Order Details',
      path: '/port-in/:requestId',
      component: <PortInOrderDetails />,
      hidden: true,
      notAllowed:
        !_meta?.permissionsMap?.[RbacModules.channelCalls]?.[RbacPermissionTypes.view] &&
        !_meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view],
    },
    {
      title: 'New Port-in Order',
      path: '/port-in/new',
      component: <PortInNewOrder />,
      hidden: true,
    },
    {
      hidden: true,
      title: 'RCS',
      path: '/rcs',
      icon: <Smartphone size={16} />,
      component: <Rcs />,
      sidebar: {
        group: SidebarGroups.deploy,
      },
    },
    {
      title: 'Secret Variables',
      path: '/secrets',
      hidden: true,
      notAllowed: !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
      icon: <SquareAsterisk size={16} />,
      component: <SecretsPage />,
      sidebar: {
        group: SidebarGroups.deploy,
      },
    },
    {
      title: 'WhatsApp',
      path: '/whatsapp',
      icon: <Whatsapp className="text-background" />,
      component: <Navigate to="/whatsapp/numbers" replace />,
      hidden: !_meta?.permissionsMap?.[RbacModules.channelWhatsapp]?.[RbacPermissionTypes.view],
      notAllowed: !_meta?.permissionsMap?.[RbacModules.channelWhatsapp]?.[RbacPermissionTypes.view],
      // Ghosted + opens the upgrade flow instead of navigating. Deep links still reach the
      // page, which renders the same CTA in place.
      locked: _meta?.isWhatsappLocked,
      lockedTooltip: LOCKED_TOOLTIP,
      onLockedClick: () => openBuddyEnterpriseRequest({ source: 'whatsapp' }),
      sidebar: {
        group: SidebarGroups.deploy,
      },
    },
    {
      title: 'WhatsApp Numbers',
      path: '/whatsapp/numbers',
      component: <WhatsappConfig />,
      hidden: true,
      notAllowed: !_meta?.permissionsMap?.[RbacModules.channelWhatsapp]?.[RbacPermissionTypes.view],
      sidebar: {
        group: SidebarGroups.deploy,
      },
    },
    {
      title: 'WhatsApp Templates',
      path: '/whatsapp/templates',
      component: <WhatsappConfig />,
      hidden: true,
      notAllowed: !_meta?.permissionsMap?.[RbacModules.channelWhatsapp]?.[RbacPermissionTypes.view],
      sidebar: {
        group: SidebarGroups.deploy,
      },
    },
    {
      title: 'WhatsApp Configurations',
      path: '/whatsapp/configurations',
      component: <WhatsappConfig />,
      hidden: true,
      notAllowed: !_meta?.permissionsMap?.[RbacModules.channelWhatsapp]?.[RbacPermissionTypes.view],
      sidebar: {
        group: SidebarGroups.deploy,
      },
    },

    // ========== MONITOR SECTION ==========
    {
      title: 'Agent Runs',
      path: '/agent-runs',
      icon: <Waypoints size={16} />,
      component: <FlowRunsPage />,
      hidden: !canViewAgents(_meta),
      notAllowed: !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
      sidebar: {
        group: SidebarGroups.monitor,
      },
    },
    {
      title: 'Agent Run Detail',
      path: '/agent-runs/:flowId/:runId',
      component: <FlowRunDetailPage />,
      hidden: true,
      notAllowed: !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
    },
    {
      title: 'Alerting',
      path: '/alerting',
      icon: <Bell size={16} />,
      component: <TabRedirect basePath="/alerting" defaultTab="voice" />,
      hidden: !canViewMonitorSection(_meta),
      notAllowed: !canViewMonitorSection(_meta),
      sidebar: {
        group: SidebarGroups.monitor,
      },
    },
    {
      title: 'Alerting',
      path: '/alerting/:tab',
      component: <CombinedAlerting />,
      hidden: true,
      notAllowed: !canViewMonitorSection(_meta),
    },
    {
      title: 'Analytics',
      path: '/analytics',
      icon: <ChartPie size={16} />,
      component: <Reports />,
      hidden: isAnalyticsHidden(_meta),
      notAllowed: isAnalyticsHidden(_meta),
      sidebar: {
        group: SidebarGroups.monitor,
      },
    },
    {
      title: 'AI Performance',
      path: '/analytics/ai-performance',
      component: <AIPerformance />,
      hidden: true,
      notAllowed:
        !_meta?.permissionsMap?.[RbacModules.report]?.[RbacPermissionTypes.view] ||
        !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
    },
    {
      title: 'Agent Availability',
      path: '/analytics/agent-availability',
      component: <AgentAvailability />,
      hidden: true,
      notAllowed: !_meta?.permissionsMap?.[RbacModules.report]?.[RbacPermissionTypes.view],
    },
    {
      title: 'Agent Performance',
      path: '/analytics/agent-performance',
      component: <AgentPerformance />,
      hidden: true,
      notAllowed: !_meta?.permissionsMap?.[RbacModules.report]?.[RbacPermissionTypes.view],
    },
    {
      title: 'Conversations Overview',
      path: '/analytics/conversations-overview',
      component: <ConversationsOverview />,
      hidden: true,
      notAllowed: !_meta?.permissionsMap?.[RbacModules.report]?.[RbacPermissionTypes.view],
    },
    {
      title: 'Customer Metrics',
      path: '/analytics/customer-metrics',
      component: <CustomerMetrics />,
      hidden: true,
      notAllowed: !_meta?.permissionsMap?.[RbacModules.report]?.[RbacPermissionTypes.view],
    },
    {
      title: 'Operations Overview',
      path: '/analytics/operations-overview',
      component: <OperationsOverview />,
      hidden: true,
      notAllowed: !_meta?.permissionsMap?.[RbacModules.report]?.[RbacPermissionTypes.view],
    },
    {
      // Optional :report?/:direction? matches /voice-agents and the split sub-reports
      // (e.g. /voice-agents/overview/inbound). The gate picks split vs legacy per the
      // voiceAgentsSplit feature flag; params default when undefined.
      title: 'Voice Agents',
      path: '/analytics/voice-agents/:report?/:direction?',
      component: <VoiceAgentsAI />,
      hidden: true,
      notAllowed:
        !_meta?.permissionsMap?.[RbacModules.report]?.[RbacPermissionTypes.view] ||
        !canViewAgents(_meta),
      accessDenialReason: agentsDenialReason(_meta),
    },
    {
      // Split into three sidebar entries (Overview/Messaging/Voice); tab comes from the route.
      title: 'Usage',
      path: '/analytics/usage/:tab?',
      component: <UsageReports />,
      hidden: true,
      notAllowed: !canViewUsageReports(_meta),
    },
    {
      title: 'Logs',
      path: '/logs',
      icon: <Logs size={16} />,
      component: <TabRedirect basePath="/logs" defaultTab="voice" />,
      hidden: !canViewLogs(_meta),
      notAllowed: !canViewLogs(_meta),
      sidebar: {
        group: SidebarGroups.monitor,
      },
    },
    {
      title: 'Logs',
      path: '/logs/:tab',
      component: <CombinedLogs />,
      hidden: true,
      notAllowed: !canViewLogs(_meta),
    },
    {
      title: 'Call Insights',
      path: '/logs/voice/:call_uuid',
      component: (
        <VoiceGated
          title="Call Insights aren't enabled on your account"
          message="Request access to view call insights and debug logs."
        >
          <CallInsights />
        </VoiceGated>
      ),
      hidden: true,
      notAllowed: !canViewLogs(_meta),
    },
    {
      title: 'Verify Session Details',
      path: '/logs/verify/:session_uuid',
      component: <VerifySessionDetail />,
      hidden: true,
      notAllowed: !canViewLogs(_meta) || _meta?.isVerifyAvailable === false,
    },
    {
      title: 'Message Insights',
      path: '/logs/messages/:message_uuid',
      component: <MessageInsights />,
      hidden: true,
      notAllowed: !canViewLogs(_meta),
    },
    {
      title: 'WhatsApp Insights',
      path: '/logs/whatsapp/:message_uuid',
      component: <MessageInsights />,
      hidden: true,
      notAllowed: !canViewLogs(_meta),
    },
    {
      title: 'SIP Trunking CDR Details',
      path: '/logs/sip-trunking/:call_uuid',
      component: <ZentrunkDetails />,
      hidden: true,
      notAllowed: !canViewLogs(_meta),
    },

    // ========== USER AVATAR POPOVER SETTINGS (Hidden from sidebar) ==========
    // Subaccounts / Messaging Settings moved to tabs on the Organization
    // settings page (SER-5859) — these old paths just redirect there now, so
    // deep links + Buddy don't 404. Geo Permissions (below) is a standalone
    // item again, not a redirect.
    {
      title: 'Subaccounts',
      path: '/sub-accounts',
      component: <Navigate to="/organization-settings/sub-accounts" replace />,
      hidden: true,
    },
    ...geoPermissionsRoutes,
    {
      title: 'Endpoints',
      path: '/endpoints',
      icon: <Settings2 size={16} />,
      // No VoiceGated: it gates on `voice_enabled === false` alone, which would still
      // block Enterprise with the flag unset. Composed in-page via useVoiceFeatureLock.
      component: <Endpoints />,
      notAllowed: !canViewBuildSection(_meta),
      // Reachable only from the user avatar popover (via Organization settings) —
      // Endpoints manage human agents and don't belong in the Build sidebar
      hidden: true,
    },

    {
      title: 'Messaging Settings',
      path: '/messaging-settings',
      component: <Navigate to="/organization-settings/messaging" replace />,
      hidden: true,
    },
    {
      // Bare path redirects into Organization settings' "Voice settings" tab.
      title: 'Voice Settings',
      path: '/voice-settings',
      component: <Navigate to="/organization-settings/voice" replace />,
      hidden: true,
    },
    {
      // Old SIP Trunking sub-tab URL — Voice Settings no longer has sub-tabs
      // (SIP trunking's consent toggle is unified into the same page), so
      // this now just renders the same merged <VoiceSettings /> page directly.
      title: 'Voice Settings - SIP Trunking',
      path: '/voice-settings/sip-trunking',
      component: <VoiceSettings />,
      hidden: true,
    },
    {
      // Old standalone SIP Trunking Settings page — merged into Voice Settings.
      title: 'SIP Trunking Settings',
      path: '/sip-trunking-settings',
      component: <Navigate to="/voice-settings/sip-trunking" replace />,
      hidden: true,
    },

    // ========== REDIRECTS FROM OLD PATHS ==========
    // Simple redirects from /api/* to new paths
    // Supports both single routes and multiple routes redirecting to the same path
    ...rows(LEGACY_REDIRECTS),
    ...legacyConsoleRoutes,
  ]
}
